import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Stack, Grid, Table, TableHead, TableRow, TableCell, TableBody, Chip, LinearProgress } from '@mui/material';
import { OptionsHeaderBar } from '../../components/options/OptionsHeaderBar';
import { optionsService } from '../../services/optionsService';
import { formatISTDateTime } from '../../utils/utils';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

const RealityComparisonPage: React.FC = () => {
  const [readiness, setReadiness] = useState<any>(null);
  const [shadow, setShadow] = useState<any>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Readiness runs a full backtest + Monte Carlo server-side, so it is fetched once per visit.
  useEffect(() => {
    optionsService
      .getReadiness('SHORT_STRANGLE', '1.0.0')
      .then(setReadiness)
      .catch((err) => setLoadError(err?.message || 'Failed to load readiness evaluation'));
  }, []);

  useEffect(() => {
    const loadShadow = () => {
      optionsService
        .getShadowStatus()
        .then(setShadow)
        .catch((err) => setLoadError(err?.message || 'Failed to load shadow status'));
    };
    loadShadow();
    const interval = setInterval(loadShadow, 5000);
    return () => clearInterval(interval);
  }, []);

  const bt = readiness?.backtestMetrics;
  const realismPct = readiness?.paperRealismGate?.realismScorePct;
  const avgSlippagePerTrade = bt && bt.totalTrades > 0 ? bt.totalSlippagePaid / bt.totalTrades : null;
  const evalTimestamp = readiness?.evaluationDate ? formatISTDateTime(readiness.evaluationDate) : formatISTDateTime(new Date());

  const fmtInr = (v: number | null | undefined) => (v != null ? `${v >= 0 ? '+' : ''}₹${v.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '—');

  return (
    <Box sx={{ p: 3, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      <OptionsHeaderBar />

      {loadError && (
        <Typography variant="body2" sx={{ color: '#DC2626', mb: 2 }}>
          {loadError}
        </Typography>
      )}

      {/* Realism Score */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', mb: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CompareArrowsIcon sx={{ color: '#0284C7' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
              BACKTEST → PAPER EXECUTION REALISM
            </Typography>
          </Stack>
          <Chip size="small" label={`Evaluated: ${evalTimestamp}`} sx={{ bgcolor: '#F1F5F9', color: '#475569', fontWeight: 600, fontSize: '0.7rem' }} />
        </Stack>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#64748B' }}>
                Backtest ➔ Paper Realism Score
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#16A34A', my: 0.5 }}>
                {realismPct != null ? `${realismPct.toFixed(1)}%` : '—'}
              </Typography>
              <LinearProgress variant="determinate" value={realismPct ?? 0} sx={{ height: 6, borderRadius: 3, bgcolor: '#E2E8F0' }} />
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#64748B' }}>
                Shadow Signals Intercepted (Live-Ready)
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#0284C7', my: 0.5 }}>
                {shadow?.totalSignalsIntercepted ?? '—'}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#64748B' }}>
                Live Trading Status
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#D97706', my: 0.5 }}>
                DISABLED
              </Typography>
            </Paper>
          </Grid>
        </Grid>
        <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mt: 2 }}>
          Paper ➔ Shadow and Shadow ➔ Live realism scores require comparable executions in each mode — not enough have run yet to compute them.
        </Typography>
      </Paper>

      {/* Cross-World Comparison Matrix */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B', mb: 2 }}>
          EXECUTION ATTRIBUTION BY MODE
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#0F172A', '& th': { color: '#FFFFFF', fontWeight: 600, fontSize: '0.72rem' } }}>
              <TableCell>EXECUTION METRIC</TableCell>
              <TableCell align="right" sx={{ bgcolor: '#1E293B' }}>
                1. BACKTEST SIM
              </TableCell>
              <TableCell align="right" sx={{ bgcolor: '#0369A1' }}>
                2. SHADOW MODE
              </TableCell>
              <TableCell align="right" sx={{ bgcolor: '#0F766E' }}>
                3. LIVE KITE EXEC
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow hover sx={{ '& td': { fontSize: '0.78rem', py: 1.2 } }}>
              <TableCell sx={{ fontWeight: 600, color: '#1E293B' }}>Avg Slippage per Trade</TableCell>
              <TableCell align="right">{avgSlippagePerTrade != null ? fmtInr(avgSlippagePerTrade) : '—'}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: '#0284C7' }}>
                {shadow?.avgSlippageErrorInr != null ? fmtInr(shadow.avgSlippageErrorInr) : '—'}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#94A3B8' }}>
                No live trades yet
              </TableCell>
            </TableRow>
            <TableRow hover sx={{ '& td': { fontSize: '0.78rem', py: 1.2 } }}>
              <TableCell sx={{ fontWeight: 600, color: '#1E293B' }}>Net P&L</TableCell>
              <TableCell align="right">{bt?.totalNetPnl != null ? fmtInr(bt.totalNetPnl) : '—'}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: '#0284C7' }}>
                {shadow?.totalShadowPnl != null ? fmtInr(shadow.totalShadowPnl) : '—'}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#94A3B8' }}>
                No live trades yet
              </TableCell>
            </TableRow>
            <TableRow hover sx={{ '& td': { fontSize: '0.78rem', py: 1.2 } }}>
              <TableCell sx={{ fontWeight: 600, color: '#1E293B' }}>Positions Closed</TableCell>
              <TableCell align="right">{bt?.totalTrades ?? '—'}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: '#0284C7' }}>
                {shadow?.closedPositionsCount ?? '—'}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#94A3B8' }}>
                0
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default RealityComparisonPage;
